import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import '@livekit/components-styles';

// Components
import ShareModal from './components/modals/ShareModal';
import RoomErrorModal from './components/modals/RoomErrorModal';
import ScheduleRoomModal from './components/modals/ScheduleRoomModal';
import RoomNotAvailableModal, {
  LinkBootstrapScreen,
  PostLeaveMeetingScreen,
} from './components/modals/RoomNotAvailableModal';
import ScheduledRoomsList from './components/ScheduledRoomsList';
import LandingPage from './components/LandingPage';
import ParticipantStateManager from './components/ParticipantStateManager';
import MeetingRoom from './components/meeting/MeetingRoom';
import MeetingHistoryPage from './components/history/MeetingHistoryPage';
import MeetingHistoryDetail from './components/history/MeetingHistoryDetail';

// Hooks
import { useControlBarStyling } from './hooks/useControlBarStyling';
import { useReconnection } from './hooks/useReconnection';
import { useNetworkMonitor } from './hooks/useNetworkMonitor';
import { useBandwidthOptimization } from './hooks/useBandwidthOptimization';
import { useEnsureChevrons } from './hooks/useEnsureChevrons';
import { useControlBarHover } from './hooks/useControlBarHover';

// Utils
import { SERVER_URL, COLORS, SPACING, BORDER_RADIUS, DEFAULT_COMPANY_ID } from './utils/constants';
import { generateRoomName, showError, showMeetingBlocked } from './utils/helpers';
import { getScheduledRoom, updateScheduledRoom, validateScheduledRoomTime, resolveJoinLink, createInstantRoom, parseRoleJoinPath, fetchSageContextOptions, setRoomSageContext, readRecruiterQuery, roomNameFromMeetingLink } from './services/roomService';
import { inviteSage, getSageStatus } from './services/copilotService';
import { normalizeSageLanguage } from './utils/sageLanguages';

// Styles
import './styles/videoConference.css';
import './styles/conferenceButtons.css';
import './styles/customControlBar.css';

function App() {
  const [recruiterContext] = useState(() => readRecruiterQuery());
  const [homeRefreshKey, setHomeRefreshKey] = useState(0);
  const [token, setToken] = useState('');
  const [roomName, setRoomName] = useState('');
  const [participantName, setParticipantName] = useState(recruiterContext.name);
  const [joinCode, setJoinCode] = useState('');
  const [livekitUrl, setLivekitUrl] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [showJoinScreen, setShowJoinScreen] = useState(false);
  const [joinRoomId, setJoinRoomId] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPeopleModal, setShowPeopleModal] = useState(false);
  const [showRoomError, setShowRoomError] = useState(false);
  const [invalidRoomId, setInvalidRoomId] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [inviteRole, setInviteRole] = useState(''); // host | team | candidate
  const [permissionDenied, setPermissionDenied] = useState('');
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isManualDisconnect, setIsManualDisconnect] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [sageInvited, setSageInvited] = useState(false);
  const [sageBusy, setSageBusy] = useState(false);
  const [sageSessionId, setSageSessionId] = useState('');
  const [resumeScoreId, setResumeScoreId] = useState('');
  const [companyId, setCompanyId] = useState(recruiterContext.companyId);
  const [candidateEmail, setCandidateEmail] = useState('');
  const [roomKind, setRoomKind] = useState('');
  const [showSageContextPicker, setShowSageContextPicker] = useState(false);
  const [sageContextOptions, setSageContextOptions] = useState([]);
  const [sageContextMeta, setSageContextMeta] = useState({
    selectedResumeScoreId: '',
    candidateName: '',
    email: '',
  });
  const [sageContextLoading, setSageContextLoading] = useState(false);
  const [sageLanguage, setSageLanguage] = useState('English');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingEndsAt, setMeetingEndsAt] = useState(null);
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showScheduledRooms, setShowScheduledRooms] = useState(false);
  const [showRoomNotAvailable, setShowRoomNotAvailable] = useState(false);
  const [scheduledRoomData, setScheduledRoomData] = useState(null);
  const [showMeetingHistory, setShowMeetingHistory] = useState(false);
  const [historyRoomName, setHistoryRoomName] = useState(null);
  const [linkResolving, setLinkResolving] = useState(() => {
    const roleJoin = parseRoleJoinPath(window.location.pathname);
    const roomParam = new URLSearchParams(window.location.search).get('room');
    return Boolean(roleJoin || roomParam);
  });
  // Invite-link leavers (no GetGeek home): rejoin vs meeting-ended screen
  const [leftMeetingSession, setLeftMeetingSession] = useState(null);
  const [isRejoining, setIsRejoining] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const storedRoomInfoRef = useRef(null); // Store room info for reconnection
  const hasGetGeekHome = Boolean(
    recruiterContext.recruiterId && recruiterContext.companyId
  );

  // Public shared Sage report (opened from GetGeek share panel / iframe)
  const sharedReportToken = useMemo(() => {
    try {
      const path = window.location.pathname || '';
      if (path.startsWith('/shared-report')) {
        return (
          new URLSearchParams(window.location.search).get('token') || ''
        ).trim();
      }
    } catch {
      /* ignore */
    }
    return '';
  }, []);

  // Disable custom control bar styling - use LiveKit's original styling
  // useControlBarStyling(isConnected);
  // useEnsureChevrons(isConnected);
  // Add hover glow effects to all control bar buttons
  useControlBarHover(isConnected);

  // Check URL path (/team/<room>, /candidate/<room>) or legacy ?room=
  useEffect(() => {
    const roleJoin = parseRoleJoinPath(window.location.pathname);
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get('room');
    if (roleJoin) {
      loadRoleJoinFromLink(roleJoin.role, roleJoin.roomName).finally(() => {
        setLinkResolving(false);
      });
    } else if (roomParam) {
      const roomName = decodeURIComponent(roomParam);
      checkScheduledRoomFromLink(roomName).finally(() => {
        setLinkResolving(false);
      });
    } else {
      setLinkResolving(false);
    }
  }, []);

  const loadRoleJoinFromLink = async (role, roomName) => {
    try {
      const invite = await resolveJoinLink(role, roomName);
      setInviteRole(invite.role || role);
      setJoinRoomId(invite.roomName || roomName);
      if (invite.title) setMeetingTitle(invite.title);
      if (invite.validation && !invite.validation.canJoin) {
        setScheduledRoomData({
          roomName: invite.roomName,
          title: invite.title,
          hostName: invite.hostName,
          scheduledStartTime: invite.scheduledStartTime,
          scheduledEndTime: invite.scheduledEndTime,
          kind: invite.kind,
        });
        setShowRoomNotAvailable(true);
        return;
      }
      setShowJoinScreen(true);
    } catch (err) {
      // Instant rooms may 404 on API before host creates record — still show join UI
      setInviteRole(role);
      setJoinRoomId(roomName);
      setShowJoinScreen(true);
      console.log('Join link resolve soft-fail:', err.message);
    }
  };

  const startSageWithResumeScore = async (scoreId, languageOverride) => {
    if (!roomName) return null;
    const language = normalizeSageLanguage(languageOverride || sageLanguage);
    setSageLanguage(language);
    setSageBusy(true);
    try {
      if (scoreId) {
        try {
          await setRoomSageContext(roomName, scoreId);
        } catch (e) {
          console.warn('Failed to persist resumeScoreId on room:', e);
        }
        setResumeScoreId(scoreId);
      }
      const result = await inviteSage({
        roomName,
        recruiter_identity: participantName,
        language,
        resume_score_id: scoreId || undefined,
      });
      setSageSessionId(result.session_id || '');
      setSageInvited(true);
      setIsCopilotOpen(true);
      if (result.session_id) {
        setRoomSageContext(roomName, scoreId || resumeScoreId || '', result.session_id).catch(
          () => { }
        );
      }
      if (result.already_running) {
        console.log('Sage already in room — attached to existing session');
      }
      return result;
    } catch (err) {
      console.error('Failed to invite Sage:', err);
      showError(err.message || 'Failed to invite Sage');
      throw err;
    } finally {
      setSageBusy(false);
    }
  };

  const handleInviteSage = async (selectedLanguage) => {
    if (!roomName || sageBusy || sageInvited) return;
    const language = normalizeSageLanguage(selectedLanguage || sageLanguage);
    setSageLanguage(language);

    // Schedule / already attached id → start immediately
    if (resumeScoreId) {
      return startSageWithResumeScore(resumeScoreId, language);
    }

    // Instant/runtime: need company + candidate email → picker (or Prompt B if none)
    if (companyId && candidateEmail) {
      setSageContextLoading(true);
      setSageBusy(true);
      try {
        const data = await fetchSageContextOptions({
          roomName,
          companyId,
          email: candidateEmail,
        });
        const options = data.options || [];
        if (options.length > 0) {
          setSageContextOptions(options);
          setSageContextMeta({
            selectedResumeScoreId: data.selectedResumeScoreId || options[0].resumeScoreId,
            candidateName: data.candidateName || '',
            email: data.email || candidateEmail,
          });
          setShowSageContextPicker(true);
          setIsCopilotOpen(true);
          return { pending_context_pick: true };
        }
        // No matches → Prompt B
        setSageBusy(false);
        return startSageWithResumeScore('', language);
      } catch (err) {
        console.error('Context options failed, starting Sage without prior:', err);
        showError(err.message || 'Could not load candidate context — starting Sage without it');
        setSageBusy(false);
        return startSageWithResumeScore('', language);
      } finally {
        setSageContextLoading(false);
        setSageBusy(false);
      }
    }

    // No company/email → Prompt B
    return startSageWithResumeScore('', language);
  };

  const handleSageContextConfirm = async (selectedId) => {
    setShowSageContextPicker(false);
    try {
      await startSageWithResumeScore(selectedId || '', sageLanguage);
    } catch {
      /* error already shown */
    }
  };

  const handleSagePresent = useCallback((info = {}) => {
    setSageInvited(true);
    if (info.sessionId) {
      setSageSessionId((prev) => prev || info.sessionId);
    }
  }, []);

  const handleSageDismissed = () => {
    setSageInvited(false);
    setSageSessionId('');
    setShowSageContextPicker(false);
    setIsCopilotOpen(true);
  };

  // Check if room from URL is scheduled and validate time
  const checkScheduledRoomFromLink = async (roomName) => {
    try {
      const roomData = await getScheduledRoom(roomName);
      if (roomData && roomData.room) {
        const room = roomData.room;
        const validation = validateScheduledRoomTime(room);

        if (!validation.canJoin) {
          // Room is scheduled but not available yet or has ended
          setScheduledRoomData(room);
          setShowRoomNotAvailable(true);
          return;
        }

        // Room is available - proceed to join screen
        setJoinRoomId(roomName);
        setShowJoinScreen(true);
      } else {
        // Not a scheduled room - proceed normally
        setJoinRoomId(roomName);
        setShowJoinScreen(true);
      }
    } catch (err) {
      // Not a scheduled room or error - proceed normally
      console.log('Not a scheduled room or error:', err.message);
      setJoinRoomId(roomName);
      setShowJoinScreen(true);
    }
  };

  // Check if a room exists and is valid
  const checkRoomExists = async (roomName) => {
    try {
      const response = await fetch(`${SERVER_URL}/api/room/check?roomName=${encodeURIComponent(roomName)}`);
      const data = await response.json();

      // If it's a scheduled room, check time validation
      if (data.isScheduled && data.validation) {
        if (!data.validation.canJoin) {
          showMeetingBlocked({
            ...data.validation,
            scheduledStartTime: data.scheduledStartTime,
            scheduledEndTime: data.scheduledEndTime,
          });
          return false;
        }
      }

      return data.isValid === true || data.exists === true;
    } catch (error) {
      console.error('Error checking room existence:', error);
      // If check fails, return false to be safe
      return false;
    }
  };

  const joinRoom = async (room, name, isHostFlag = false, options = {}) => {
    try {
      const roleHint = options.role || inviteRole || (isHostFlag ? 'host' : '');
      const quietBlocked = Boolean(options.quietBlocked);

      if (!roleHint) {
        try {
          const scheduledRoomData = await getScheduledRoom(room);
          if (scheduledRoomData && scheduledRoomData.room) {
            const scheduledRoom = scheduledRoomData.room;
            const isHostUser = isHostFlag || scheduledRoom.hostName === name;
            const validation = validateScheduledRoomTime(scheduledRoom, new Date(), isHostUser);
            if (!validation.canJoin) {
              if (!quietBlocked) {
                showMeetingBlocked({
                  ...validation,
                  title: scheduledRoom.title,
                });
              }
              return { ok: false, reason: validation.reason };
            }
          }
        } catch (err) {
          console.log('Not a scheduled room or error:', err.message);
        }

        if (!isHostFlag) {
          const roomExists = await checkRoomExists(room);
          if (!roomExists) {
            setInvalidRoomId(room);
            setShowRoomError(true);
            setShowJoinScreen(false);
            return { ok: false, reason: 'not_found' };
          }
        }
      }

      const response = await fetch(`${SERVER_URL}/api/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomName: room,
          participantName: name,
          isHost: roleHint === 'host' || (isHostFlag && roleHint !== 'team'),
          role: roleHint || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403 && (data.reason === 'upcoming' || data.reason === 'ended')) {
          if (!quietBlocked) showMeetingBlocked(data);
          return { ok: false, reason: data.reason };
        }
        if (response.status === 403 && data.reason === 'candidate_taken') {
          setPermissionDenied(
            data.message || "You don't have permission to join as candidate."
          );
          setShowJoinScreen(false);
          return { ok: false, reason: 'candidate_taken' };
        }
        if (response.status === 404) {
          setInvalidRoomId(room);
          setShowRoomError(true);
          setShowJoinScreen(false);
          return { ok: false, reason: 'not_found' };
        }
        throw new Error(data.error || data.message || 'Failed to get token');
      }

      const hostStatus = data.role === 'host';
      setRoomName(data.roomName || room);
      setToken(data.token);
      setLivekitUrl(data.url);
      setIsConnected(true);
      setIsHost(hostStatus);
      if (data.title) {
        setMeetingTitle(data.title);
      } else if (scheduledRoomData?.title) {
        setMeetingTitle(scheduledRoomData.title);
      }
      if (data.sageJoin) {
        setSageInvited(true);
        setIsCopilotOpen(true);
        // Attach to existing Sage if auto-join already dispatched it.
        getSageStatus(data.roomName || room)
          .then((status) => {
            if (status?.session_id) setSageSessionId(status.session_id);
            if (status?.already_running || status?.status === 'running') {
              setSageInvited(true);
            }
          })
          .catch(() => { });
      }
      if (data.resumeScoreId) {
        setResumeScoreId(data.resumeScoreId);
      } else {
        setResumeScoreId('');
      }
      setCompanyId(data.companyId || '');
      setCandidateEmail(data.candidateEmail || '');
      setRoomKind(data.kind || '');
      setMeetingEndsAt(data.scheduledEndTime || null);
      setShowJoinScreen(false);
      setShowRoomError(false);
      setIsReconnecting(false);
      setPermissionDenied('');

      storedRoomInfoRef.current = {
        roomName: data.roomName || room,
        participantName: name,
        isHost: hostStatus,
        role: data.role || roleHint,
        token: data.token,
        livekitUrl: data.url,
      };

      window.history.replaceState({}, document.title, '/');
      return { ok: true };
    } catch (error) {
      console.error('Error joining room:', error);
      if (error.message && !error.message.includes('404')) {
        showError(error.message || "Couldn't join the meeting");
      }
      return { ok: false };
    }
  };

  const handleNewMeeting = async (nameOverride) => {
    const name = String(typeof nameOverride === 'string' ? nameOverride : participantName).trim();

    if (!name) {
      showError('Please enter your name');
      return;
    }
    if (isCreatingMeeting) return;

    setParticipantName(name);
    setIsCreatingMeeting(true);
    try {
      const newRoomName = generateRoomName();
      const created = await createInstantRoom({
        roomName: newRoomName,
        hostName: name,
        title: 'Instant meeting',
        sageJoin: false,
        companyId: companyId || recruiterContext.companyId || DEFAULT_COMPANY_ID || undefined,
        recruiterId: recruiterContext.recruiterId || undefined,
      });
      if (created.room?.companyId) {
        setCompanyId(created.room.companyId);
      } else if (!companyId && !recruiterContext.companyId && DEFAULT_COMPANY_ID) {
        setCompanyId(DEFAULT_COMPANY_ID);
      }
      setInviteRole('host');
      await joinRoom(newRoomName, name, true, {
        role: 'host',
      });
      if (created.room?.hostLink || created.room?.roomName) {
        setShowShareModal(true);
      }
    } catch (err) {
      showError(err.message || 'Failed to create meeting');
    } finally {
      setIsCreatingMeeting(false);
    }
  };

  const handleJoinWithCode = async (nameOverride) => {
    const name = String(typeof nameOverride === 'string' ? nameOverride : participantName).trim();

    if (!joinCode.trim() || !name) {
      showError('Please enter a code or link and your name');
      return;
    }
    if (isJoining) return;

    const room = joinCode.trim();
    setParticipantName(name);
    setIsJoining(true);
    try {
      await joinRoom(room, name);
    } finally {
      setIsJoining(false);
    }
  };

  const handleJoinCalendarEvent = async (event, nameOverride) => {
    const name = String(nameOverride || recruiterContext.name || participantName || 'Host').trim();
    const room = event?.roomName || roomNameFromMeetingLink(event?.hostLink);
    if (!room) {
      if (event?.hostLink) {
        window.open(event.hostLink, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    setParticipantName(name);
    setInviteRole('host');
    setIsJoining(true);
    try {
      // Calendar (GetGeek) is source of truth for display/join when opening from the list.
      try {
        if (event?.start && event?.end) {
          try {
            await updateScheduledRoom(room, {
              scheduledStartTime: event.start,
              scheduledEndTime: event.end,
              syncCalendar: false,
            });
          } catch (syncErr) {
            console.log('Calendar→room time sync soft-fail:', syncErr.message);
          }
        }

        const scheduled = await getScheduledRoom(room);
        const scheduledRoom = scheduled?.room;
        if (scheduledRoom) {
          const validation = validateScheduledRoomTime(scheduledRoom, new Date(), true);
          if (!validation.canJoin) {
            showMeetingBlocked({
              ...validation,
              title: event?.title || scheduledRoom.title,
              scheduledStartTime:
                event?.start || scheduledRoom.scheduledStartTime || validation.scheduledStartTime,
              scheduledEndTime:
                event?.end || scheduledRoom.scheduledEndTime || validation.scheduledEndTime,
            });
            return;
          }
        }
      } catch (err) {
        console.log('Calendar event room lookup soft-fail:', err.message);
      }
      await joinRoom(room, name, true, { role: 'host' });
    } finally {
      setIsJoining(false);
    }
  };

  const handleJoinFromLink = async (e) => {
    e.preventDefault();

    if (!participantName.trim()) {
      showError('Please enter your name');
      return;
    }

    if (!joinRoomId) {
      showError('Invalid room link');
      return;
    }
    if (isJoining) return;

    setIsJoining(true);
    try {
      await joinRoom(joinRoomId, participantName, inviteRole === 'host', {
        role: inviteRole,
      });
    } finally {
      setIsJoining(false);
    }
  };
  const handleBackToHome = () => {
    setShowJoinScreen(false);
    setShowRoomError(false);
    setShowRoomNotAvailable(false);
    setJoinRoomId('');
    setInvalidRoomId('');
    setScheduledRoomData(null);
    setParticipantName('');
    setInviteRole('');
    setPermissionDenied('');
    setLeftMeetingSession(null);
    window.history.replaceState({}, document.title, '/');
  };

  const handleRejoinFromLeave = async (session) => {
    if (!session?.roomName || isRejoining) return;
    const name = String(session.participantName || '').trim();
    if (!name) {
      setJoinRoomId(session.roomName);
      setInviteRole(session.role || '');
      setLeftMeetingSession(null);
      setShowJoinScreen(true);
      return;
    }
    setIsRejoining(true);
    try {
      setInviteRole(session.role || '');
      setParticipantName(name);
      const result = await joinRoom(session.roomName, name, Boolean(session.isHost), {
        role: session.role || (session.isHost ? 'host' : 'team'),
        quietBlocked: true,
      });
      if (result?.ok) {
        setLeftMeetingSession(null);
        return;
      }
      if (result?.reason === 'ended') {
        setLeftMeetingSession((prev) =>
          prev
            ? { ...prev, scheduledEndTime: new Date(0).toISOString() }
            : prev
        );
      }
    } finally {
      setIsRejoining(false);
    }
  };

  // Handle reconnection callback
  const handleReconnect = useCallback((success) => {
    if (success) {
      setIsReconnecting(false);
      console.log('Reconnection successful');
    } else {
      setIsReconnecting(false);
      // If reconnection failed after max attempts, show error
      showError('Connection lost. Please refresh the page to reconnect.');
    }
  }, []);

  const handleLiveKitConnected = useCallback(() => {
    setIsConnected((connected) => {
      if (!connected) {
        console.log('LiveKit connected - updating isConnected state');
      }
      return true;
    });
    setIsReconnecting(false);
  }, []);

  // Monitor network changes - but don't interfere with LiveKit's reconnection
  // Enhanced for mobile devices which may have different network transition behaviors
  const handleNetworkChange = useCallback((status, networkInfo) => {
    if (status === 'offline') {
      console.log('Network went offline - LiveKit will handle reconnection');
      // Don't set isReconnecting here - let LiveKit handle it
    } else if (status === 'online') {
      console.log('Network back online - LiveKit will reconnect automatically');
      // LiveKit will automatically reconnect, don't interfere
    } else if (status === 'type-changed' && isConnected) {
      // Mobile devices frequently change network types (WiFi <-> Cellular)
      // Log the change but don't interfere - LiveKit handles this automatically
      const networkType = networkInfo?.type || networkInfo?.effectiveType || 'unknown';
      console.log('Network type changed on mobile:', networkType, networkInfo);
      // Bandwidth optimization hook will handle quality adjustment
      // Don't trigger reconnection - LiveKit handles network changes seamlessly
    }
  }, [isConnected]);

  useNetworkMonitor(handleNetworkChange);

  const handleDisconnect = (reason) => {
    console.log('Disconnected from room, reason:', reason, 'type:', typeof reason);

    // LiveKit DisconnectReason enum values:
    // 0 = UNKNOWN, 1 = CLIENT_INITIATED, 2 = DUPLICATE_IDENTITY, 3 = SERVER_SHUTDOWN
    // 4 = PARTICIPANT_REMOVED, 5 = ROOM_DELETED, 6 = STATE_MISMATCH, 7 = JOIN_FAILURE
    // 8 = MIGRATION, 9 = SIGNAL_CLOSED, 10 = SIGNAL_ERROR

    // Check if reason is a number (enum) or string
    let isManual = false;

    if (typeof reason === 'number') {
      // Numeric enum: 1 = CLIENT_INITIATED (manual disconnect)
      isManual = reason === 1;
    } else if (reason) {
      // String or object - convert to string for comparison
      const reasonStr = reason.toString();
      const reasonUpper = reasonStr.toUpperCase();
      isManual = reasonUpper.includes('CLIENT_INITIATED') ||
        (reasonUpper.includes('SERVER_INITIATED') && !reasonUpper.includes('NETWORK'));
    }
    // If reason is undefined/null, treat as network issue (not manual)

    if (isManual) {
      // Manual disconnect (Leave button clicked)
      console.log('Manual disconnect detected (reason:', reason, '), clearing state');

      // Invite / direct join (no GetGeek home): keep enough to show rejoin or ended
      const postLeave =
        !hasGetGeekHome && roomName
          ? {
              roomName,
              participantName,
              role: inviteRole || (isHost ? 'host' : 'team'),
              isHost,
              title: meetingTitle || 'Meeting',
              scheduledEndTime: meetingEndsAt || null,
            }
          : null;

      setIsReconnecting(false);
      setIsManualDisconnect(true);
      setToken('');
      setLivekitUrl('');
      setRoomName('');
      setIsConnected(false);
      setIsHost(false);
      setIsCopilotOpen(false);
      setSageInvited(false);
      setSageSessionId('');
      setSageLanguage('English');
      setResumeScoreId('');
      setCompanyId(hasGetGeekHome ? (recruiterContext.companyId || '') : '');
      setCandidateEmail('');
      setRoomKind('');
      setMeetingEndsAt(null);
      setShowSageContextPicker(false);
      setSageContextOptions([]);
      setMeetingTitle('');
      storedRoomInfoRef.current = null;
      setLeftMeetingSession(postLeave);
    } else {
      // Network issue, undefined reason, or other disconnect - let LiveKit handle reconnection automatically
      // DO NOT clear state - LiveKit will reconnect using the existing token and connection
      console.log('Network or unknown disconnect detected (reason:', reason, '), LiveKit will handle automatic reconnection');
      console.log('Preserving state for automatic reconnection:', { roomName, participantName, hasToken: !!token });

      // Mark as NOT manual disconnect so reconnection can proceed
      setIsManualDisconnect(false);

      // Don't set isReconnecting here - let LiveKit's connectionStateChanged event handle it
      // This prevents showing "Reconnecting..." when LiveKit is already handling it

      // Store room info for potential manual reconnection if LiveKit completely fails
      if (!storedRoomInfoRef.current && roomName && participantName) {
        storedRoomInfoRef.current = {
          roomName,
          participantName,
          isHost,
          role: inviteRole || (isHost ? 'host' : 'team'),
          token,
          livekitUrl
        };
        console.log('Stored room info for potential reconnection');
      }

      // LiveKit will automatically attempt reconnection with its built-in policy
      // We don't need to do anything here - just preserve state
    }
  };

  // Attempt manual reconnection only if LiveKit fails (fallback)
  const attemptReconnection = useCallback(async () => {
    if (!storedRoomInfoRef.current) return;

    // Check if already reconnected
    if (isConnected) {
      setIsReconnecting(false);
      return;
    }

    const {
      roomName: storedRoom,
      participantName: storedName,
      isHost: storedIsHost,
      role: storedRole,
    } = storedRoomInfoRef.current;

    console.log('Attempting manual reconnection to room:', storedRoom);

    try {
      // Get a new token
      const response = await fetch(`${SERVER_URL}/api/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomName: storedRoom,
          participantName: storedName,
          isHost: storedIsHost,
          role: storedRole || (storedIsHost ? 'host' : undefined),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get reconnection token');
      }

      // Update token and reconnect
      setToken(data.token);
      setLivekitUrl(data.url);
      if (data.scheduledEndTime) {
        setMeetingEndsAt(data.scheduledEndTime);
      }
      setIsConnected(true);
      setIsReconnecting(false);
      console.log('Manual reconnection successful');
    } catch (error) {
      console.error('Manual reconnection failed:', error);
      // Don't clear state - let user try again or refresh
    }
  }, [isConnected]);

  // Styles
  const appContainerStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: 'url(/background.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    padding: SPACING.xl,
  };

  const contentContainerStyle = {
    width: '100%',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const videoConferenceContainerStyle = {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    background: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
  };

  // Join Meeting Screen Styles
  const joinScreenContainerStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: SPACING.xl,
  };

  const joinCardStyle = {
    width: '100%',
    maxWidth: '480px',
    background: COLORS.background,
    borderRadius: BORDER_RADIUS.xl,
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: SPACING.xxxxl,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  };

  const backButtonStyle = {
    position: 'absolute',
    top: SPACING.xl,
    left: SPACING.xl,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: SPACING.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: BORDER_RADIUS.md,
    transition: 'background 0.2s ease',
  };

  const envelopeIconContainerStyle = {
    width: '64px',
    height: '64px',
    borderRadius: BORDER_RADIUS.full,
    background: COLORS.primaryGradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: SPACING.xxl,
  };

  const joinTitleStyle = {
    fontSize: '28px',
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: SPACING.sm,
    textAlign: 'center',
  };

  const roomIdStyle = {
    fontSize: '14px',
    color: COLORS.textSecondary,
    marginBottom: SPACING.xxl,
    textAlign: 'center',
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: COLORS.text,
    marginBottom: SPACING.sm,
    alignSelf: 'flex-start',
    width: '100%',
  };

  const joinInputStyle2 = {
    width: '100%',
    padding: `${SPACING.lg} ${SPACING.lg}`,
    border: `1px solid ${COLORS.primary}`,
    borderRadius: BORDER_RADIUS.md,
    background: COLORS.background,
    color: COLORS.text,
    fontSize: '16px',
    marginBottom: SPACING.xxl,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
  };

  const joinMeetingButtonStyle = {
    width: '100%',
    padding: `${SPACING.lg} ${SPACING.xxl}`,
    background: COLORS.primaryGradient,
    color: COLORS.background,
    border: 'none',
    borderRadius: BORDER_RADIUS.md,
    fontSize: '16px',
    fontWeight: '500',
    cursor: isJoining ? 'wait' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
    transition: 'all 0.2s ease',
    opacity: isJoining ? 0.85 : 1,
  };

  const permissionNoteStyle = {
    fontSize: '12px',
    color: COLORS.textTertiary,
    marginTop: SPACING.lg,
    textAlign: 'center',
  };

  // Render Room Error Screen
  if (permissionDenied) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: COLORS.backgroundGrey,
        padding: 24,
      }}>
        <div style={{
          maxWidth: 440,
          width: '100%',
          background: COLORS.background,
          borderRadius: 12,
          padding: 32,
          textAlign: 'center',
        }}>
          <h2 style={{ color: COLORS.error, marginTop: 0 }}>Permission denied</h2>
          <p style={{ color: COLORS.textSecondary }}>{permissionDenied}</p>
          <button
            type="button"
            onClick={handleBackToHome}
            style={{
              marginTop: 16,
              padding: '12px 20px',
              background: COLORS.primary,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (showRoomError) {
    return (
      <RoomErrorModal
        roomId={invalidRoomId}
        onReturnHome={handleBackToHome}
      />
    );
  }

  // Handle join from RoomNotAvailableModal (host/team — never candidate)
  const handleJoinFromNotAvailableModal = async (roomName, name, isHost) => {
    const joinName = (name || '').trim();
    if (!joinName) {
      setInviteRole('host');
      setJoinRoomId(roomName);
      setShowRoomNotAvailable(false);
      setShowJoinScreen(true);
      return;
    }
    setInviteRole('host');
    setShowRoomNotAvailable(false);
    await joinRoom(roomName, joinName, true, { role: 'host' });
  };

  // Render Room Not Available Screen (for scheduled rooms accessed too early/ended)
  if (showRoomNotAvailable && scheduledRoomData) {
    return (
      <RoomNotAvailableModal
        isOpen={showRoomNotAvailable}
        onClose={handleBackToHome}
        room={scheduledRoomData}
        onBackToHome={handleBackToHome}
        onJoinRoom={handleJoinFromNotAvailableModal}
        participantName={participantName}
      />
    );
  }

  // Invite-link leave: rejoin while live, or meeting ended
  if (leftMeetingSession?.roomName) {
    return (
      <PostLeaveMeetingScreen
        session={leftMeetingSession}
        onRejoin={handleRejoinFromLeave}
        rejoining={isRejoining}
      />
    );
  }

  if (sharedReportToken) {
    return (
      <div className="mg-history-root">
        <MeetingHistoryDetail
          roomName=""
          shareToken={sharedReportToken}
          readOnly
        />
      </div>
    );
  }

  // Avoid flashing landing while resolving /team|/candidate|?room= links
  if (linkResolving) {
    return <LinkBootstrapScreen />;
  }

  if (showMeetingHistory) {
    const scopeCompanyId = recruiterContext.companyId || companyId;
    const scopeRecruiterId = recruiterContext.recruiterId;
    if (historyRoomName) {
      return (
        <div className="mg-history-root">
          <MeetingHistoryDetail
            roomName={historyRoomName}
            companyId={scopeCompanyId}
            recruiterId={scopeRecruiterId}
            onBack={() => setHistoryRoomName(null)}
          />
        </div>
      );
    }
    return (
      <div className="mg-history-root">
        <MeetingHistoryPage
          companyId={scopeCompanyId}
          recruiterId={scopeRecruiterId}
          onBack={() => {
            setShowMeetingHistory(false);
            setHistoryRoomName(null);
          }}
          onOpenMeeting={(room) => setHistoryRoomName(room)}
        />
      </div>
    );
  }

  // Render Join Meeting Screen
  if (showJoinScreen && joinRoomId) {
    return (
      <div style={joinScreenContainerStyle}>
        <div style={joinCardStyle}>
          {/* <button
            style={backButtonStyle}
            onClick={handleBackToHome}
            onMouseEnter={(e) => e.target.style.background = COLORS.backgroundGrey}
            onMouseLeave={(e) => e.target.style.background = 'none'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke={COLORS.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button> */}

          <div style={envelopeIconContainerStyle}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="5" width="18" height="14" rx="2" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M3 7L12 13L21 7" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h2 style={joinTitleStyle}>
            {inviteRole === 'host'
              ? 'Join as host'
              : inviteRole === 'candidate'
                ? 'Join as candidate'
                : inviteRole === 'team'
                  ? 'Join as teammate'
                  : 'Join Meeting'}
          </h2>
          <p style={roomIdStyle}>Room ID: <strong>{joinRoomId}</strong></p>

          <form onSubmit={handleJoinFromLink} style={{ width: '100%' }}>
            <input
              type="text"
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
              placeholder="Enter your name"
              required
              style={joinInputStyle2}
              disabled={isJoining}
              onFocus={(e) => e.target.style.borderColor = COLORS.primary}
              onBlur={(e) => e.target.style.borderColor = COLORS.primary}
            />

            <button
              type="submit"
              style={joinMeetingButtonStyle}
              disabled={isJoining}
              aria-busy={isJoining}
            >
              {isJoining ? (
                <>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.35)',
                      borderTopColor: '#fff',
                      display: 'inline-block',
                      animation: 'spin 0.7s linear infinite',
                    }}
                    aria-hidden
                  />
                  <span>Joining…</span>
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="5" width="18" height="14" rx="2" stroke="white" strokeWidth="1.5" fill="none" />
                    <path d="M3 7L12 13L21 7" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Join Meeting</span>
                </>
              )}
            </button>
          </form>
          <p style={permissionNoteStyle}>You'll need to allow camera and microphone access to join</p>
        </div>
      </div>
    );
  }

  if (isConnected && token && livekitUrl) {
    return (
      <div style={videoConferenceContainerStyle}>
        <LiveKitRoom
          video={true}
          audio={true}
          token={token}
          serverUrl={livekitUrl}
          data-lk-theme="default"
          onDisconnected={handleDisconnect}
          options={{
            // Enable adaptive bitrate for better low bandwidth handling
            adaptiveStream: true,
            // Enable dynacast for better quality adaptation
            dynacast: true,
            // Set publish defaults for low bandwidth scenarios
            publishDefaults: {
              videoCodec: 'vp8', // VP8 is better for low bandwidth
              videoEncoding: {
                maxBitrate: 2000000, // 2 Mbps max, will adapt down
                maxFramerate: 30
              }
            },
            // Let LiveKit use its default reconnection policy
            // Custom reconnectPolicy structure was causing errors
            // LiveKit handles reconnection automatically with built-in retry logic
          }}
          connect={true}
          style={{ height: '100%', width: '100%', background: '#ffffff' }}
        >
          <ReconnectionWrapper
            onReconnect={handleReconnect}
            attemptReconnection={attemptReconnection}
            isReconnecting={isReconnecting}
            isManualDisconnect={isManualDisconnect}
            onConnected={handleLiveKitConnected}
          >
            <BandwidthOptimizationWrapper />
            <ParticipantStateManager />
            <MeetingRoom
              roomName={roomName}
              participantName={participantName}
              meetingTitle={meetingTitle}
              isHost={isHost}
              scheduledEndTime={meetingEndsAt}
              onLeave={() => {
                setIsManualDisconnect(true);
              }}
              sageInvited={sageInvited}
              sageBusy={sageBusy}
              onInviteSage={handleInviteSage}
              sageSessionId={sageSessionId}
              onSagePresent={handleSagePresent}
              onSageDismissed={handleSageDismissed}
              showPeopleModal={showPeopleModal}
              setShowPeopleModal={setShowPeopleModal}
              setShowShareModal={setShowShareModal}
              isChatOpen={isChatOpen}
              setIsChatOpen={setIsChatOpen}
              isCopilotOpen={isCopilotOpen}
              setIsCopilotOpen={setIsCopilotOpen}
              sageContextPicker={{
                open: showSageContextPicker,
                props: {
                  options: sageContextOptions,
                  selectedResumeScoreId: sageContextMeta.selectedResumeScoreId,
                  candidateName: sageContextMeta.candidateName,
                  email: sageContextMeta.email,
                  loading: sageContextLoading,
                  onConfirm: handleSageContextConfirm,
                  onCancel: () => {
                    setShowSageContextPicker(false);
                    if (!sageInvited) setIsCopilotOpen(false);
                  },
                },
              }}
            />
            <RoomAudioRenderer />
          </ReconnectionWrapper>
        </LiveKitRoom>
        <ShareModal
          roomName={roomName}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          hostName={participantName}
          candidateEmail={candidateEmail}
          onCandidateEmailSaved={(email) => setCandidateEmail(email || '')}
        />
      </div>
    );
  }

  // Handle scheduled room join (team/host role from Scheduled Meetings list)
  const handleJoinScheduledRoom = async (roomName, name, isHost) => {
    setInviteRole('host');
    await joinRoom(roomName, name, true, { role: 'host' });
  };

  // After schedule link modal "Done": refresh landing only (no "Link Copied" popup).
  const handleScheduleSuccess = () => {
    setShowScheduleModal(false);
    setHomeRefreshKey((key) => key + 1);
  };

  // Show scheduled rooms list
  if (showScheduledRooms) {
    return (
      <div style={appContainerStyle}>
        <div style={contentContainerStyle}>
          <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
            <button
              onClick={() => setShowScheduledRooms(false)}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.text,
                fontSize: '16px',
                cursor: 'pointer',
                marginBottom: SPACING.lg,
                display: 'flex',
                alignItems: 'center',
                gap: SPACING.sm,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke={COLORS.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Home
            </button>
            <ScheduledRoomsList
              onJoinRoom={handleJoinScheduledRoom}
              participantName={participantName}
            />
          </div>
        </div>
        <ScheduleRoomModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onSuccess={handleScheduleSuccess}
          companyId={recruiterContext.companyId || companyId}
          recruiterId={recruiterContext.recruiterId}
          defaultHostName={recruiterContext.name || participantName}
        />
      </div>
    );
  }

  // Landing requires ?recruiterId=&companyId= (GetGeek). Missing → gate in LandingPage.
  return (
    <>
      <LandingPage
        recruiterId={recruiterContext.recruiterId}
        companyId={recruiterContext.companyId}
        recruiterName={recruiterContext.name}
        joinCode={joinCode}
        onJoinCodeChange={setJoinCode}
        onJoin={handleJoinWithCode}
        onStartInstant={handleNewMeeting}
        onScheduleLater={() => setShowScheduleModal(true)}
        onJoinEvent={handleJoinCalendarEvent}
        onResolvedName={(resolved) => {
          const next = String(resolved || '').trim();
          if (next) setParticipantName((prev) => (prev?.trim() ? prev : next));
        }}
        onViewMeetingHistory={() => {
          setHistoryRoomName(null);
          setShowMeetingHistory(true);
        }}
        onOpenHistoryMeeting={(room) => {
          setHistoryRoomName(room);
          setShowMeetingHistory(true);
        }}
        isCreatingMeeting={isCreatingMeeting}
        isJoining={isJoining}
        refreshKey={homeRefreshKey}
      />
      <ScheduleRoomModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSuccess={handleScheduleSuccess}
        companyId={recruiterContext.companyId || companyId}
        recruiterId={recruiterContext.recruiterId}
        defaultHostName={recruiterContext.name || participantName}
      />
    </>
  );
}

// Wrapper component to use hooks inside LiveKitRoom context
function ReconnectionWrapper({ children, onReconnect, attemptReconnection, isReconnecting, isManualDisconnect, onConnected }) {
  const previousStateRef = useRef(null);
  const onReconnectRef = useRef(onReconnect);
  const onConnectedRef = useRef(onConnected);
  onReconnectRef.current = onReconnect;
  onConnectedRef.current = onConnected;

  const handleReconnectionResult = useCallback((success) => {
    // This callback is called by useReconnection when max retries are reached
    if (!success) {
      // Max reconnection attempts reached - only notify parent, don't clear state here
      console.log('Max reconnection attempts reached in useReconnection hook');
      onReconnectRef.current?.(false);
    }
  }, []);

  const { connectionState } = useReconnection(handleReconnectionResult, 20); // Increased retries to allow LiveKit more time to reconnect

  // Monitor connection state changes from LiveKit
  useEffect(() => {
    // Only process state changes, not initial null state
    if (connectionState === null) return;

    // Only act on state transitions, not repeated same state
    if (previousStateRef.current === connectionState) return;

    console.log('Connection state transition:', previousStateRef.current, '->', connectionState);
    previousStateRef.current = connectionState;

    if (connectionState === 'connected') {
      // LiveKit has successfully connected/reconnected
      console.log('LiveKit connected/reconnected successfully');
      onReconnectRef.current?.(true);
      onConnectedRef.current?.();
    } else if (connectionState === 'reconnecting' && !isManualDisconnect) {
      // LiveKit is actively reconnecting - show indicator
      console.log('LiveKit is reconnecting...');
    } else if (connectionState === 'disconnected' && !isManualDisconnect) {
      // Disconnected but not manual - LiveKit will try to reconnect
      console.log('LiveKit disconnected, will attempt reconnection');
    }
  }, [connectionState, isManualDisconnect]);

  // Show reconnecting indicator when:
  // 1. isReconnecting is true (set by parent or this component)
  // 2. It's not a manual disconnect
  // 3. Connection state is reconnecting or disconnected
  const shouldShowReconnecting = (connectionState === 'reconnecting' ||
    (connectionState === 'disconnected' && !isManualDisconnect)) &&
    !isManualDisconnect;

  if (shouldShowReconnecting) {
    return (
      <>
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: COLORS.overlayDark,
          color: COLORS.background,
          padding: `${SPACING.md} ${SPACING.xl}`,
          borderRadius: BORDER_RADIUS.md,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: SPACING.md
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            border: `2px solid ${COLORS.background}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span>Reconnecting...</span>
        </div>
        {children}
      </>
    );
  }

  return <>{children}</>;
}

// Wrapper for bandwidth optimization hook
function BandwidthOptimizationWrapper() {
  useBandwidthOptimization();
  return null;
}

export default App;
